import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { WidgetSize } from '../../../types/Dashboard';
import { useAuth } from '../../../contexts/AuthContext';
import { firebaseService } from '../../../services/firebaseService';

interface ComparisonWidgetProps {
  config: {
    comparisonType?: string;
  };
  size: WidgetSize;
}

const ComparisonWidget: React.FC<ComparisonWidgetProps> = ({ config, size }) => {
  const { userProfile } = useAuth();
  const [current, setCurrent] = useState<number>(0);
  const [previous, setPrevious] = useState<number>(0);
  const [label, setLabel] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadComparisonData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.comparisonType, userProfile?.empresaCnpj]);

  const loadComparisonData = async () => {
    if (!userProfile?.empresaCnpj || !config.comparisonType) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const mensalidades = await firebaseService.getAllMensalidades(userProfile.empresaCnpj);
      const hoje = new Date();
      const mesAtual = hoje.getMonth();
      const anoAtual = hoje.getFullYear();
      const mesAnterior = new Date(anoAtual, mesAtual - 1, 1);

      switch (config.comparisonType) {
        case 'mes-atual-vs-anterior':
          setLabel('Este Mês vs Mês Anterior');
          const receitaMesAtual = mensalidades
            .filter(m => {
              const dataVencimento = new Date(m.dataVencimento);
              return dataVencimento.getMonth() === mesAtual && 
                     dataVencimento.getFullYear() === anoAtual &&
                     m.dataPagamento && m.dataPagamento !== '';
            })
            .reduce((sum, m) => sum + (m.valorPago || m.valor), 0);
          const receitaMesAnterior = mensalidades
            .filter(m => {
              const dataVencimento = new Date(m.dataVencimento);
              return dataVencimento.getMonth() === mesAnterior.getMonth() && 
                     dataVencimento.getFullYear() === mesAnterior.getFullYear() &&
                     m.dataPagamento && m.dataPagamento !== '';
            })
            .reduce((sum, m) => sum + (m.valorPago || m.valor), 0);
          setCurrent(receitaMesAtual);
          setPrevious(receitaMesAnterior);
          break;
        default:
          setLabel('Comparativo');
          setCurrent(0);
          setPrevious(0);
      }
    } catch (error) {
      console.error('Erro ao carregar dados comparativos:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300"></div>
      </div>
    );
  }

  const change = previous > 0 ? ((current - previous) / previous) * 100 : (current > 0 ? 100 : 0);
  const changeAbs = Math.abs(change);

  return (
    <div className="h-full flex flex-col justify-center min-h-0">
      <p className="text-sm font-medium text-gray-500 mb-4">{label}</p>
      <div className="space-y-4">
        <div>
          <p className="text-xs text-gray-500 mb-1">Este Mês</p>
          <p className="text-2xl font-bold text-gray-900">
            R$ {current.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Mês Anterior</p>
          <p className="text-xl font-semibold text-gray-600">
            R$ {previous.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
          {change > 0 && <TrendingUp className="h-5 w-5 text-success-600" />}
          {change < 0 && <TrendingDown className="h-5 w-5 text-danger-600" />}
          {change === 0 && <Minus className="h-5 w-5 text-gray-400" />}
          <span className={`text-lg font-semibold ${change > 0 ? 'text-success-600' : change < 0 ? 'text-danger-600' : 'text-gray-400'}`}>
            {change > 0 ? '+' : ''}{changeAbs.toFixed(1)}%
          </span>
        </div>
      </div>
    </div>
  );
};

export default ComparisonWidget;

